import { useEffectOnce } from 'react-use';
import { useCallback } from 'react';

import { useUrlSearchState } from './useUrlSearchState';
import { useAppContext } from './useAppContext';
import { useSearchStore } from './stores/search/useSearchStore';
import { usePromptStore } from './stores/search/usePromptStore';
import { SearchMode } from './stores/search/slices/searchModeSlice';
import { createSearchStateFromUrl, isNonEmptyObject, resolveInitialSortingOptionId } from '../utils/searchStateUtils';
import { mapSearchListingsFiltersToSearchState } from '../utils/mapFilterFields';

export const useSetSearchStateFromUrlOnMount = () => {
  const {
    initialFilters,
    willSyncStateWithUrl,
    options: initialOptions,
    featureFlags,
    initialSearchMode,
    smartSortPreferences,
  } = useAppContext();
  const { setIsSetStateFromUrlDone, isSetStateFromUrlDone } = useUrlSearchState();
  const applyState = useSearchStore((state) => state.applyState);
  const setPromptValue = usePromptStore((state) => state.setPromptValue);
  const hasSmartSortPreferences = isNonEmptyObject(smartSortPreferences);

  const setStateFromUrl = useCallback(() => {
    if (!willSyncStateWithUrl) return;

    const {
      filters,
      options,
      placeName: placeNameFromUrl,
      searchMode: searchModeFromUrl,
      mapMode,
      sortingOptionId: sortingOptionIdFromUrl,
    } = createSearchStateFromUrl(window.location.href, initialFilters, initialOptions);
    const shouldApplySearchMode = searchModeFromUrl && (searchModeFromUrl !== SearchMode.AI || featureFlags.isAiSearchEnabled);
    const nextSearchMode = shouldApplySearchMode ? searchModeFromUrl : initialSearchMode;
    const hasAiSearchPreferences = isNonEmptyObject(filters.preferences);

    const sortingOptionId = resolveInitialSortingOptionId({
      sortingOptionId: sortingOptionIdFromUrl,
      propertyMarketingType: filters.propertyMarketingType[0],
      searchMode: nextSearchMode,
      isSmartSortEnabled: featureFlags.isSmartSortEnabled,
      hasSmartSortPreferences,
      hasAiSearchPreferences,
    });

    const newState = mapSearchListingsFiltersToSearchState({
      filters,
      options,
      mapMode,
      sortingOptionId,
    });

    if (placeNameFromUrl) {
      newState.placeName = placeNameFromUrl;
    }
    if (nextSearchMode) {
      newState.searchMode = nextSearchMode;
      newState.modalSearchMode = nextSearchMode;
    }

    if (newState.searchMode !== SearchMode.AI) {
      setPromptValue('');
    }

    applyState({ state: newState, shouldCallBeforeFiltersChangeCallback: false });
  }, [
    willSyncStateWithUrl,
    initialFilters,
    initialOptions,
    featureFlags,
    initialSearchMode,
    hasSmartSortPreferences,
    applyState,
    setPromptValue,
  ]);

  useEffectOnce(() => {
    // prevent double initialization in Strict Mode.
    if (isSetStateFromUrlDone) return;

    setStateFromUrl();
    setIsSetStateFromUrlDone(true);

    // disable browser scroll restoration since we handle it manually
    history.scrollRestoration = 'manual';
    window.addEventListener('popstate', setStateFromUrl);

    return () => {
      history.scrollRestoration = 'auto';
      window.removeEventListener('popstate', setStateFromUrl);
      setIsSetStateFromUrlDone(false);
    };
  });
};
